// ─────────────────────────────────────────────────────────────
// JerryMarkerLanes — renders Group, GroupSustain, and Section
// lanes above the timeline based on the project's player type.
//
// Visibility rules:
//   song / loop  → section only
//   preacher     → groupSustain + group + section
//   loopBuilder  → all hidden
//
// Subscribes to the project profile so it loads the correct
// marker data and updates lane visibility whenever a project
// is opened or closed.
// ─────────────────────────────────────────────────────────────

import css from "./JerryMarkerLane.sass?inline"
import {Lifecycle, UUID} from "@opendaw/lib-std"
import {createElement, Frag} from "@opendaw/lib-jsx"
import {Html} from "@opendaw/lib-dom"
import {StudioService} from "@/service/StudioService.ts"
import {JerryMarkerLaneHeader} from "./JerryMarkerLaneHeader"
import {JerryMarkerLaneBody} from "./JerryMarkerLaneBody"
import {jerryMarkerStore} from "./JerryMarkerStore"
import {STProjectMeta} from "@/jerry/st-panel/STTypes"

const className = Html.adoptStyleSheet(css, "jerry-marker-lane")

type Construct = {
    lifecycle: Lifecycle
    service: StudioService
}

const readPlayerType = (meta: Record<string, unknown>): STProjectMeta["st_playerType"] =>
    (meta.st_playerType as STProjectMeta["st_playerType"]) ?? "song"

const visibilityFor = (pt: STProjectMeta["st_playerType"]) => ({
    groupSustain: pt === "preacher",
    group:        pt === "preacher",
    section:      pt !== "loopBuilder",
})

export const JerryMarkerLanes = ({lifecycle, service}: Construct) => {

    const initialMeta = service.hasProfile
        ? service.profile.meta as STProjectMeta & Record<string, unknown>
        : {} as Record<string, unknown>
    const initial = visibilityFor(readPlayerType(initialMeta))

    // Group Sustain lane (top)
    const groupSustainLane: HTMLElement = (
        <div className={className} style={{display: initial.groupSustain ? "" : "none"}}>
            <JerryMarkerLaneHeader lane="groupSustain"/>
            <div class="void"/>
            <JerryMarkerLaneBody lifecycle={lifecycle} service={service} lane="groupSustain"/>
        </div>
    )

    // Group lane (middle)
    const groupLane: HTMLElement = (
        <div className={className} style={{display: initial.group ? "" : "none"}}>
            <JerryMarkerLaneHeader lane="group"/>
            <div class="void"/>
            <JerryMarkerLaneBody lifecycle={lifecycle} service={service} lane="group"/>
        </div>
    )

    // Section lane (bottom)
    const sectionLane: HTMLElement = (
        <div className={className} style={{display: initial.section ? "" : "none"}}>
            <JerryMarkerLaneHeader lane="section"/>
            <div class="void"/>
            <JerryMarkerLaneBody lifecycle={lifecycle} service={service} lane="section"/>
        </div>
    )

    // React immediately when the user changes playerType in the Song Info panel
    const onPlayerTypeChange = (e: Event) => {
        const pt = (e as CustomEvent<STProjectMeta["st_playerType"]>).detail
        const vis = visibilityFor(pt)
        groupSustainLane.style.display = vis.groupSustain ? "" : "none"
        groupLane.style.display        = vis.group        ? "" : "none"
        sectionLane.style.display      = vis.section      ? "" : "none"
    }
    document.addEventListener("st:playertype", onPlayerTypeChange)
    lifecycle.own({terminate: () => document.removeEventListener("st:playertype", onPlayerTypeChange)})

    // Load marker data and update lane visibility whenever the project changes
    lifecycle.own(
        service.projectProfileService.catchupAndSubscribe(profileOption => {
            profileOption.match({
                none: () => {
                    jerryMarkerStore.unload()
                    const vis = visibilityFor("song")
                    groupSustainLane.style.display = vis.groupSustain ? "" : "none"
                    groupLane.style.display        = vis.group        ? "" : "none"
                    sectionLane.style.display      = vis.section      ? "" : "none"
                },
                some: profile => {
                    jerryMarkerStore.load(UUID.toString(profile.uuid))
                    const pt = readPlayerType(profile.meta as Record<string, unknown>)
                    const vis = visibilityFor(pt)
                    groupSustainLane.style.display = vis.groupSustain ? "" : "none"
                    groupLane.style.display        = vis.group        ? "" : "none"
                    sectionLane.style.display      = vis.section      ? "" : "none"
                }
            })
        })
    )

    return (
        <Frag>
            {groupSustainLane}
            {groupLane}
            {sectionLane}
        </Frag>
    )
}
